/**
 * shipping.ts — Centralized shipping configuration for H2HUMANS.
 *
 * Single source of truth for shipping costs, thresholds, and constants.
 */

/** Shipping cost in EUR (used when order is below the free threshold). */
export const SHIPPING_COST_EUR = 4.99;

/** Shipping cost in cents (used for Stripe). */
export const SHIPPING_COST_CENTS = 499;

/** Free shipping threshold in EUR (orders at or above this amount ship free). */
export const FREE_SHIPPING_THRESHOLD_EUR = 50;

/** Free shipping threshold in cents. */
export const FREE_SHIPPING_THRESHOLD_CENTS = 5000;

/** The Daily Routine Komplett-Set (bundle) always ships free. */
export const BUNDLE_ALWAYS_FREE_SHIPPING = true;

/** Delivery time in working days, used everywhere (product pages, FAQ, /versand, AGB, Stripe, JSON-LD). */
export const DELIVERY_DAYS_MIN = 3;
export const DELIVERY_DAYS_MAX = 5;

/** "3–5 Werktage" (nominative, e.g. "Die Lieferzeit beträgt 3–5 Werktage."). */
export function deliveryTimeText(): string {
  return `${DELIVERY_DAYS_MIN}–${DELIVERY_DAYS_MAX} Werktage`;
}

/** "3–5 Werktagen" (dative, e.g. "Lieferung in 3–5 Werktagen"). */
export function deliveryTimeDative(): string {
  return `${DELIVERY_DAYS_MIN}–${DELIVERY_DAYS_MAX} Werktagen`;
}

/** Countries we deliver to, with their carrier. `code` maps to translated country names. */
export const DELIVERY_COUNTRIES = [
  { code: "DE", land: "Deutschland", carrier: "DHL" },
] as const;

/** Stripe and website share the same delivery-country allowlist. */
export const ALLOWED_SHIPPING_COUNTRIES = DELIVERY_COUNTRIES.map(({ code }) => code);

/** All shipping partners. */
export const CARRIERS = ["DHL"] as const;

/** "Stand" date of the shipping terms (/versand). */
export const SHIPPING_TERMS_UPDATED = "Oktober 2026";

/** Shows the Shop-AGB block on /agb once the full shop terms are published. */
export const SHOP_TERMS_PUBLISHED = false;

/** German euro format: 4,99 € / 50 € (whole amounts without decimals). */
export function formatEuro(value: number): string {
  const n = Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(".", ",");
  return `${n}\u00a0€`;
}

/**
 * Shipping for a whole cart. The Daily Routine Komplett-Set (bundle) and its
 * packages always ship free; a promo code may waive shipping; otherwise the
 * free threshold applies to the discounted total.
 */
export function calcCartShippingCents(opts: {
  hasBundle: boolean;
  promoFreeShipping: boolean;
  discountedTotalCents: number;
}): number {
  if ((opts.hasBundle && BUNDLE_ALWAYS_FREE_SHIPPING) || opts.promoFreeShipping) return 0;
  return opts.discountedTotalCents >= FREE_SHIPPING_THRESHOLD_CENTS ? 0 : SHIPPING_COST_CENTS;
}
